import { Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { useNavigate, Link } from "react-router-dom";
import { navigateToPricing } from "@/utils/navigation";
import { DailyCircusBanner } from "@/components/DailyCircusBanner";
const data = {
  facebookLink: 'https://www.facebook.com/profile.php?id=61565941284952',
  instaLink: 'https://www.instagram.com/gimmefy.ai/',
  linkedinLink: 'https://www.linkedin.com/company/gimmefy-ai',
  youtubeLink: 'https://www.youtube.com/@gimmefy',
  services: {
    webdev: '/agents',
    webdesign: '/studio',
    marketing: '/playbooks',
    visuallab: '/visual-lab'
  },
  about: {
    history: '/about',
    team: '/about',
    careers: '/about',
    enterprise: '/enterprise'
  },
  help: {
    faqs: '/about',
    support: '/about',
    docs: '/about',
    contact: '/about'
  },
  contact: {
    email: 'hello@gimmefy.ai',
    phone: '+1 (555) 123-4567',
    address: 'San Francisco, CA, USA'
  },
  company: {
    name: 'gimmefy',
    description: 'Create end-to-end marketing content with an AI specialist trained to bring your vision to life, deliver results that work better, and ship campaigns faster.',
    logo: '/placeholder.svg'
  }
};
const socialLinks = [{
  icon: Linkedin,
  label: 'LinkedIn',
  href: data.linkedinLink
}, {
  icon: Youtube,
  label: 'YouTube',
  href: data.youtubeLink
}, {
  icon: Instagram,
  label: 'Instagram',
  href: data.instaLink
}, {
  icon: Facebook,
  label: 'Facebook',
  href: data.facebookLink
}];
const gettingAheadLinks = [{
  text: 'Free 360 Brand Report',
  href: '/get-free-brand-report'
}, {
  text: 'Create Content Calendars with AI',
  href: '/create-social-content-calendars-with-ai'
}, {
  text: 'Launch Marketing Campaigns with AI',
  href: '/launch-marketing-campaigns-with-ai'
}, {
  text: 'Generate Blog Articles with AI',
  href: '/generate-blog-articles-with-ai'
}];
const customerLinks = [{
  text: 'Marketing AI for Startups',
  href: '/startups'
}, {
  text: 'Marketing AI for Content Creators',
  href: '/creators-and-influencers'
}, {
  text: 'Marketing AI for Retailers',
  href: '/retail'
}, {
  text: 'Marketing AI for Freelancers',
  href: '/freelancers'
}, {
  text: 'Self Serve Pricing',
  href: '#',
  onClick: true
}, {
  text: 'Take a Comic Break',
  href: '/daily-circus'
}];
const serviceLinks = [{
  text: 'Studio',
  href: data.services.webdesign
}, {
  text: 'Playbooks',
  href: data.services.marketing
}, {
  text: 'Visual Lab',
  href: data.services.visuallab
}, {
  text: 'AI Agents',
  href: data.services.webdev
}, {
  text: 'AI-Powered Ad Showcase',
  href: '/ai-ad-gallery'
}];
const enterpriseLinks = [{
  text: 'gimmefy for Enterprise',
  href: '/enterprise#enterprise-overview'
}, {
  text: 'Custom AI Integration',
  href: '/enterprise#ai-lab'
}, {
  text: 'Hire gimmefy Expert',
  href: '/enterprise#creation-desk'
}, {
  text: 'Contact Sales',
  href: '/enterprise#meet-the-crew'
}, {
  text: 'Free Enterprise Demo',
  href: '/enterprise#enterprise-form'
}, {
  text: 'ROI Calculator',
  href: '/roi-calculator'
}, {
  text: 'Security & Privacy',
  href: '/enterprise#enterprise-security'
}];
const helpfulLinks = [{
  text: 'Login',
  href: '/login',
  external: true
}, {
  text: 'Knowledge Base',
  href: 'https://knowledgebase.gimmefy.ai/portal/en/kb/gimmefy-ai',
  external: true
}, {
  text: 'Blog',
  href: 'https://www.blog.gimmefy.ai/',
  external: true
}, {
  text: 'About gimmefy',
  href: '/about'
}, {
  text: 'Contact us',
  href: '/contact'
}, {
  text: 'Tell a friend',
  href: '/tellafriend'
}, {
  text: 'Terms of Use',
  href: '/terms-of-use',
  internal: true
}, {
  text: 'Privacy Policy',
  href: '/privacy-policy',
  internal: true
}];
export default function FooterWithNewsletter() {
  const navigate = useNavigate();
  const scrollToTop = () => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };
  return <footer className="bg-gradient-hero-background relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-subtle opacity-30"></div>

      <div className="relative z-10 mx-auto max-w-screen-xl px-4 pt-16 pb-6 sm:px-6 lg:px-8">
        {/* First Row: Company Info & Daily Circus Banner */}
        <div className="flex flex-col md:grid md:grid-cols-2 gap-8 md:gap-12 mb-12">
          {/* Left Column: Company Info - 50% */}
          <div className="text-left">
            <a href="/" className="text-primary inline-flex justify-start gap-2" id="footer-logo" aria-label="Go to homepage">
              <img src="https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/c0fc8354-2e53-474d-a206-279098ebdfd1/Artboard+20.png?content-type=image%2Fpng" alt="gimmefy logo" className="h-8 w-auto" width={165} height={32} />
            </a>

            <p className="text-muted-foreground mt-6 leading-relaxed max-w-2xl">
              {data.company.description}
            </p>

            <ul className="mt-8 flex justify-start gap-6 md:gap-8">
              {socialLinks.map(({
              icon: Icon,
              label,
              href
            }) => <li key={label}>
                  <a href={href} target="_blank" rel="noopener noreferrer" id={`footer-social-${label.toLowerCase()}`} className="text-primary hover:text-primary/80 transition">
                    <span className="sr-only">{label}</span>
                    <Icon className="size-6" />
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Right Column: Daily Circus Banner - 50% */}
          <div className="flex justify-center lg:justify-center items-start">
            <DailyCircusBanner />
          </div>
        </div>

        {/* Footer Links */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5">
          <div className="text-left">
            <p className="text-lg font-medium text-foreground">Enterprise Fusion</p>
            <ul className="mt-8 space-y-4 text-sm">
              {enterpriseLinks.map(({
              text,
              href
            }) => <li key={text}>
                  <Link to={href} onClick={scrollToTop} id={`footer-enterprise-${text.toLowerCase().replace(/\s+/g, '-')}`} className="text-muted-foreground hover:text-foreground transition">
                    {text}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="text-left">
            <p className="text-lg font-medium text-foreground">Marketing Features</p>
            <ul className="mt-8 space-y-4 text-sm">
              {serviceLinks.map(({
              text,
              href
            }) => <li key={text}>
                  <Link to={href} onClick={scrollToTop} id={`footer-service-${text.toLowerCase().replace(/\s+/g, '-')}`} className="text-muted-foreground hover:text-foreground transition">
                    {text}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="text-left">
            <p className="text-lg font-medium text-foreground">Built for Humans</p>
            <ul className="mt-8 space-y-4 text-sm">
              {customerLinks.map(({
              text,
              href,
              onClick
            }) => <li key={text}>
                  {onClick && text === 'Self Serve Pricing' ? (
                    <button id={`footer-customer-${text.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => navigateToPricing(navigate)} className="text-muted-foreground hover:text-foreground transition">
                      {text}
                    </button>
                  ) : (
                    <Link to={href} onClick={scrollToTop} id={`footer-customer-${text.toLowerCase().replace(/\s+/g, '-')}`} className="text-muted-foreground hover:text-foreground transition">
                      {text}
                    </Link>
                  )}
                </li>)}
            </ul>
          </div>

          <div className="text-left">
            <p className="text-lg font-medium text-foreground">Co-create with AI</p>
            <ul className="mt-8 space-y-4 text-sm">
              {gettingAheadLinks.map(({
              text,
              href
            }) => <li key={text}>
                  <Link to={href} onClick={scrollToTop} id={`footer-getting-ahead-${text.toLowerCase().replace(/\s+/g, '-')}`} className="text-muted-foreground hover:text-foreground transition">
                    {text}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="text-left">
            <p className="text-lg font-medium text-foreground">Know More</p>
            <ul className="mt-8 space-y-4 text-sm">
              {helpfulLinks.map(({
              text,
              href,
              external,
              internal
            }) => <li key={text}>
                  {text === 'Contact us' || text === 'About gimmefy' || text === 'Tell a friend' ? <Link to={href} onClick={scrollToTop} id={`footer-helpful-${text.toLowerCase().replace(/\s+/g, '-')}`} className="text-muted-foreground hover:text-foreground transition">
                      {text}
                    </Link> : internal ? <Link to={href} onClick={scrollToTop} id={`footer-helpful-${text.toLowerCase().replace(/\s+/g, '-')}`} className="text-muted-foreground hover:text-foreground transition">
                      {text}
                    </Link> : <a href={href} onClick={scrollToTop} id={`footer-helpful-${text.toLowerCase().replace(/\s+/g, '-')}`} className="text-muted-foreground hover:text-foreground transition" {...external && {
                target: "_blank",
                rel: "noopener noreferrer"
              }}>
                      {text}
                    </a>}
                </li>)}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 border-t border-white/20 pt-6">
          <div className="text-left sm:flex sm:justify-between">
            <p className="text-sm text-muted-foreground">
              <span className="block sm:inline">All rights reserved.</span>
            </p>
            <p className="text-muted-foreground mt-4 text-sm sm:order-first sm:mt-0">
              &copy; 2025 {data.company.name}
            </p>
          </div>
        </div>
      </div>
    </footer>;
}